import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmailConnectionCard from './components/EmailConnectionCard';
import EmailThreadItem from './components/EmailThreadItem';
import EmailComposer from './components/EmailComposer';
import EmailSearchBar from './components/EmailSearchBar';
import EmailStats from './components/EmailStats';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const EmailsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectedAccounts, setConnectedAccounts] = useState({ gmail: false, outlook: false });
  const { profile, organization } = useAuth();

  const fetchEmails = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      let query = supabase?.from('emails')?.select('*, contacts(first_name, last_name)')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: false })?.limit(50);

      if (searchQuery) {
        query = query?.or(`subject.ilike.%${searchQuery}%,from_address.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      const mapped = (data || [])?.map(e => ({
        id: e?.id,
        sender: e?.from_address?.split('@')?.[0] || 'Unknown',
        senderEmail: e?.from_address || '',
        account: e?.contacts ? `${e?.contacts?.first_name} ${e?.contacts?.last_name}` : '',
        subject: e?.subject,
        preview: e?.body?.substring(0, 120) || '',
        date: new Date(e.created_at),
        isRead: true,
        hasAttachment: false,
        labels: [],
        body: e?.body || ''
      }));

      setEmails(mapped);
    } catch (err) {
      console.error('Emails fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, searchQuery]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  // Check connected integrations from org settings
  useEffect(() => {
    if (organization?.settings) {
      const settings = organization?.settings;
      setConnectedAccounts({
        gmail: !!settings?.gmail_connected,
        outlook: !!settings?.outlook_connected
      });
    }
  }, [organization]);

  const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleEmailClick = (email) => setSelectedEmail(email);

  const handleSendEmail = async (emailData) => {
    try {
      await supabase?.from('emails')?.insert({
        org_id: profile?.org_id,
        subject: emailData?.subject,
        body: emailData?.body,
        from_address: profile?.email,
        to_address: emailData?.to,
        status: 'sent'
      });
      fetchEmails();
      setIsComposerOpen(false);
    } catch (err) {
      console.error('Send email error:', err);
    }
  };

  const handleConnectProvider = async (provider) => {
    // Store connection state in org settings
    try {
      const newSettings = { ...(organization?.settings || {}), [`${provider}_connected`]: true };
      await supabase?.from('organizations')?.update({ settings: newSettings })?.eq('id', profile?.org_id);
      setConnectedAccounts(prev => ({ ...prev, [provider]: true }));
    } catch (err) {
      console.error('Connect provider error:', err);
    }
  };

  const emailStats = {
    total: emails?.length,
    unread: emails?.filter(e => !e?.isRead)?.length,
    today: emails?.filter(e => new Date(e.date)?.toDateString() === new Date()?.toDateString())?.length,
    connectedAccounts: Object.values(connectedAccounts)?.filter(Boolean)?.length
  };

  const hasConnectedAccounts = Object.values(connectedAccounts)?.some(Boolean);

  return (
    <>
      <Helmet>
        <title>Emails - Wilcart CRM</title>
        <meta name="description" content="Manage email communications in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="h-[calc(100vh-4rem)] flex flex-col">
            <div className="bg-background border-b border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Emails</h1>
                  <p className="text-muted-foreground mt-1">Manage your email communications and integrations</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="default"
                    onClick={() => setIsComposerOpen(true)}
                    disabled={!hasConnectedAccounts}
                  >
                    <Icon name="Plus" size={16} className="mr-2" />
                    Compose Email
                  </Button>
                </div>
              </div>
            </div>

            {!hasConnectedAccounts ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-4xl w-full">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Mail" size={32} className="text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">Connect Your Email Accounts</h2>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Integrate with Gmail or Outlook to manage all your customer communications in one place
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <EmailConnectionCard
                      provider="Gmail"
                      icon="Mail"
                      description="Connect your Gmail account to sync emails and manage threads directly from Wilcart CRM."
                      onConnect={() => handleConnectProvider('gmail')}
                      isConnected={connectedAccounts?.gmail}
                    />
                    <EmailConnectionCard
                      provider="Outlook"
                      icon="Mail"
                      description="Connect your Outlook account to sync emails and manage threads directly from Wilcart CRM."
                      onConnect={() => handleConnectProvider('outlook')}
                      isConnected={connectedAccounts?.outlook}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex overflow-hidden">
                <div className="w-full lg:w-2/5 border-r border-border flex flex-col">
                  <div className="p-4 border-b border-border">
                    <EmailStats stats={emailStats} />
                    <EmailSearchBar
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      onSearch={setSearchQuery}
                      onFilterChange={() => {}}
                    />
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {loading ? (
                      <div className="p-4 space-y-3">
                        {[1,2,3]?.map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}
                      </div>
                    ) : emails?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                        <Icon name="Mail" size={32} className="text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">No emails found</p>
                      </div>
                    ) : (
                      emails?.map(email => (
                        <EmailThreadItem
                          key={email?.id}
                          email={email}
                          isSelected={selectedEmail?.id === email?.id}
                          onClick={() => handleEmailClick(email)}
                        />
                      ))
                    )}
                  </div>
                </div>
                <div className="hidden lg:flex flex-1 flex-col">
                  {selectedEmail ? (
                    <div className="p-6">
                      <h2 className="text-xl font-semibold text-foreground mb-2">{selectedEmail?.subject}</h2>
                      <p className="text-sm text-muted-foreground mb-4">From: {selectedEmail?.senderEmail}</p>
                      <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap">{selectedEmail?.body}</div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                      <Icon name="Mail" size={48} className="text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Select an email to read</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>

        {isComposerOpen && (
          <EmailComposer
            isOpen={isComposerOpen}
            onClose={() => setIsComposerOpen(false)}
            onSend={handleSendEmail}
          />
        )}
      </div>
    </>
  );
};

export default EmailsPage;